import { Icon } from '../../../components/icons/Icon'
import './FixturesResultsList.css'

/**
 * UX design-stage scaffold (IB-210 Slice 2, demo v66 — see
 * `docs/design/IB-210-cartridge-studio-ui-design.md` §4.6/§4.7.2).
 * PRESENTATIONAL ONLY: no `fetch`, no internal state. The Developer wires
 * `onRun` to the real `POST .../fixture-runs` endpoints
 * (`EligibilityModelController.runFixtures` /
 * `CalcTestFixtureController.runFixtures`) and maps the real
 * `FixtureRunSummaryView` / `CalcFixtureRunSummaryView` response shapes onto
 * `FixtureResultRow[]`.
 *
 * `mode` is the load-bearing prop this component exists to get right:
 *   - "gates-publish"  → `EligibilityModel` only. A failing fixture is a REAL,
 *     server-enforced publish block (`EligibilityAuthoringService.publishModel`
 *     422s). Renders the "Publish blocked" banner.
 *   - "informational"  → `CalculationGraph` only (ADR-0020, RAID R-19). A
 *     failing fixture here blocks NOTHING — `CalcGraphService.create` performs
 *     no fixture check. This mode's copy must NEVER say "publish blocked" —
 *     that line existed in the original prototype pass and does not match the
 *     merged backend (IB-210 DoR finding #2). Do not restore it.
 */
export type FixturesRunnerMode = 'gates-publish' | 'informational'

export interface FixtureResultRow {
  fixtureId: string
  /** A short human label for the row — e.g. the fixture's own name/id, or a one-line summary of its inputs. */
  label: string
  expected: string
  actual: string
  passed: boolean
}

export interface FixturesResultsListProps {
  mode: FixturesRunnerMode
  /** `null` before the first run this session. */
  results: FixtureResultRow[] | null
  /** `null` before the first run; mirrors the API's `allPassed` (vacuously true when there are zero fixtures). */
  allPassed: boolean | null
  /** Extra disclosure text appended after the mode's standard banner — e.g. the "informational" mode's
   * published-Parameter-only caveat (DoR finding #3). */
  disclosure?: string
  onRun: () => void
  running?: boolean
}

export function FixturesResultsList({ mode, results, allPassed, disclosure, onRun, running }: FixturesResultsListProps) {
  const passCount = results?.filter((r) => r.passed).length ?? 0
  const failCount = results ? results.length - passCount : 0

  return (
    <div className="iw-fixtures">
      <div className="iw-fixtures__summary">
        {results ? (
          <>
            <span className="iw-pill iw-pill--success">{passCount} pass</span>
            {failCount > 0 ? <span className="iw-pill iw-pill--danger">{failCount} fail</span> : null}
          </>
        ) : (
          <span className="iw-fixtures__empty">Not yet run</span>
        )}
        <button type="button" className="iw-btn iw-btn--sm" onClick={onRun} disabled={running}>
          <Icon name="refresh-cw" size={13} />
          {running ? 'Running…' : 'Run fixtures'}
        </button>
      </div>

      {results?.map((row) => (
        <div key={row.fixtureId} className={row.passed ? 'iw-fixtures__row' : 'iw-fixtures__row iw-fixtures__row--fail'}>
          <span
            className={row.passed ? 'iw-fixtures__dot iw-fixtures__dot--pass' : 'iw-fixtures__dot iw-fixtures__dot--fail'}
            aria-hidden="true"
          />
          <span className="iw-fixtures__label">{row.label}</span>
          <span className="iw-fixtures__result">
            expected {row.expected} · got {row.actual}
          </span>
        </div>
      ))}

      {mode === 'gates-publish' && allPassed === false ? (
        <div className="iw-inline-alert iw-inline-alert--error" role="alert">
          <Icon name="alert-circle" size={14} />
          Publish blocked — {failCount} fixture{failCount === 1 ? '' : 's'} failing. Fix the fixture or the
          draft, then re-run.
        </div>
      ) : null}

      {mode === 'informational' ? (
        <div className="iw-inline-alert iw-inline-alert--info" role="status">
          <Icon name="info" size={14} />
          Informational only — a red fixture here never blocks the next version from becoming effective
          (ADR-0020). {disclosure}
        </div>
      ) : null}
    </div>
  )
}
